import React, {
  useEffect,
  useRef,
  MutableRefObject,
  useCallback,
  useState,
} from "react";
import { Box } from "rebass/styled-components";
import videojs from "video.js";
import "videojs-contrib-ads";
import "videojs-ima";
import "videojs-contrib-quality-levels";
import "video.js/dist/video-js.css";
import "videojs-ima/dist/videojs.ima.css";
import { StyledPlayer } from "./styledComponents";
import { analytics } from "../Analytics";
import { useFullscreen } from "../Hooks/useFullscreen";
import { isMobileOnly } from "react-device-detect";

import { useAds } from "../Providers";

import { buildAdTag } from "../Utilities/Ads";

import "./index.css";
export type TSourceType = { src: string; type: string };

interface IVideoPlayerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onPlay" | "onError"> {
  mp4Source?: string;
  hlsSource?: string;
  watchAnalyticsProperties: Record<string, unknown>;
  playAnalyticsProperties: Record<string, unknown>;
  options?: Omit<videojs.PlayerOptions, "loop">;
  refForVideoPlayer: MutableRefObject<videojs.Player | null>;
  onPlayStart?: (arg0?: videojs.Player | null) => void;
  onPlayEnd?: (arg0?: videojs.Player | null) => void;
  onPlayPause?: (arg0?: videojs.Player | null) => void;
  onPlay?: (arg0?: videojs.Player | null) => void;
  onReady?: (arg0?: videojs.Player | null) => void;
  onMuteChange?: (muted: boolean) => void;
  // video.js media playback error (e.g. the source 404s) — distinct from the
  // DOM onError this interface already inherits from HTMLAttributes, which
  // never fires for video.js since it manages its own <video> internally.
  onError?: (arg0?: videojs.Player | null) => void;
  loop?: boolean; // @IMPORTANT use this to loop instead of options.loop
  eventSrc?: string;
  wrapperId?: string;
  onAdStarted?: () => void;
  onAdCompleted?: () => void;
  duration?: number;
  volumeStorage?: "session" | "local";
  ads?: boolean;
  // When set, >=1080p renditions also unlock once the player's rendered
  // width crosses this value, independent of the Fullscreen API — some
  // embed hosts (e.g. U.GG) pop the iframe into a larger modal instead of
  // calling requestFullscreen(), so isFullscreen alone never fires there.
  hdMinWidth?: number;
}

export const VideoPlayer = ({
  mp4Source,
  hlsSource,
  options,
  refForVideoPlayer,
  onPlayStart,
  onPlayEnd,
  onPlayPause,
  onPlay,
  onReady,
  onMuteChange,
  onError,
  onAdStarted,
  onAdCompleted,
  watchAnalyticsProperties,
  playAnalyticsProperties,
  loop,
  eventSrc,
  wrapperId,
  volumeStorage = "session",
  duration,
  ads = false,
  hdMinWidth,
  ...props
}: IVideoPlayerProps) => {
  const videoRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<videojs.Player | null>(null);
  const wrapperRef = useRef<HTMLElement | null>(null);
  const [secondsWatched, setSecondsWatched] = useState(0);
  const [trackedView, setTrackedView] = useState(false);
  const [isWideEnoughForHd, setIsWideEnoughForHd] = useState(false);
  const interval = useRef<ReturnType<typeof setInterval> | undefined>();
  const { imaStatus, config: adsConfig } = useAds();

  const { isFullscreen, toggleFullscreen } = useFullscreen(wrapperRef, {
    onEnter: () => {
      analytics.track("Button - Click", {
        ...watchAnalyticsProperties,
        locationIdentifier: "Video Player - Fullscreen",
      });
    },
  });

  const interceptFullscreenClick = useCallback(() => {
    const fullscreen = document.getElementsByClassName(
      "vjs-fullscreen-control",
    );

    if (fullscreen.length && wrapperId && !isMobileOnly) {
      const originalButton = fullscreen[0];
      const clonedFullscreen = originalButton.cloneNode(true);
      clonedFullscreen.addEventListener("click", async () => {
        const wrapper = document.getElementById(wrapperId);
        if (!wrapper) return;

        wrapperRef.current = wrapper;

        try {
          await toggleFullscreen();
        } catch (error) {
          console.error(error);
        }
      });

      originalButton.replaceWith(clonedFullscreen);
    }
  }, [wrapperId, toggleFullscreen]);

  const startTrackingSecondsWatched = useCallback(() => {
    if (!interval.current) {
      interval.current = setInterval(() => {
        setSecondsWatched((prev) => prev + 1);
      }, 1000);
    }
  }, [interval]);

  const stopTrackingSecondsWatched = useCallback(() => {
    clearInterval(interval.current);
    interval.current = undefined;
  }, [interval]);

  useEffect(() => {
    // Don't create player until IMA is ready if video ads are enabled
    if (ads && adsConfig.video.enabled && imaStatus === "loading") {
      return;
    }

    if (!playerRef.current && videoRef.current) {
      const videoElement = document.createElement("video-js");
      videoElement.classList.add("vjs-big-play-centered");
      videoRef.current.appendChild(videoElement);

      const player = videojs(videoElement, {
        controls: true,
        fluid: true,
        sources: [],
        playsinline: true,
        ...options,
      });

      playerRef.current = player;
    }

    if (refForVideoPlayer) {
      refForVideoPlayer.current = playerRef.current;
      if (onReady) onReady(playerRef.current);
    }
  }, [
    options,
    refForVideoPlayer,
    onReady,
    imaStatus,
    adsConfig.video.enabled,
    ads,
  ]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || (ads && adsConfig.video.enabled && imaStatus === "loading"))
      return;

    player.one("ready", () => {
      if (ads && adsConfig.video.enabled && imaStatus === "ready") {
        if (typeof player.ima !== "function") {
          return;
        }

        const adTagUrl = buildAdTag({
          ...adsConfig.video,
          duration: duration ?? 0,
          clipGame: watchAnalyticsProperties.game as string,
        });

        player.ima({
          adTagUrl,
        });

        if (!player.ima.initializeAdDisplayContainer) {
          return;
        }

        player.ima.initializeAdDisplayContainer();

        player.on("adstart", () => {
          onAdStarted?.();
        });

        player.on("adend", () => {
          onAdCompleted?.();
        });
        player.on("ads-ad-skipped", () => {
          onAdCompleted?.();
        });
        player.on("aderror", () => {
          onAdCompleted?.();
        });
      }

      try {
        interceptFullscreenClick();
      } catch (error) {
        console.error(error);
      }
    });
  }, [
    imaStatus,
    interceptFullscreenClick,
    options?.autoplay,
    duration,
    onAdStarted,
    onAdCompleted,
    adsConfig.video,
    ads,
  ]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || (ads && adsConfig.video.enabled && imaStatus === "loading"))
      return;

    let source: TSourceType | null = null;

    if (mp4Source) {
      source = { src: mp4Source, type: "video/mp4" };
    } else if (hlsSource) {
      source = { src: hlsSource, type: "application/x-mpegURL" };
    }

    if (source) {
      setSecondsWatched(0);
      setTrackedView(false);
      stopTrackingSecondsWatched();

      if (options?.poster) {
        player.poster(options.poster);
      }

      if (
        ads &&
        adsConfig.video.enabled &&
        player.ima?.setContentWithAdTag &&
        player.ima?.requestAds
      ) {
        const adTagUrl = buildAdTag({
          ...adsConfig.video,
          duration: duration ?? 0,
          clipGame: watchAnalyticsProperties.game as string,
        });

        try {
          player.ima.setContentWithAdTag(source.src, adTagUrl);
          player.ima.requestAds();
        } catch (error) {
          const currentSource = player.currentSrc();
          if (currentSource !== source.src) {
            player.src(source);
            player.load();
          }
        }
      } else {
        const currentSource = player.currentSrc();
        if (currentSource !== source.src) {
          player.src(source);
          player.load();
        }
      }
    }
  }, [
    mp4Source,
    hlsSource,
    stopTrackingSecondsWatched,
    imaStatus,
    duration,
    options?.poster,
    adsConfig.video,
    ads,
  ]);

  useEffect(() => {
    if (hdMinWidth === undefined) return;

    // The embed CSS sizes the player off the viewport (vw/vh), not its
    // parent's layout box, so viewport width is the accurate signal here —
    // it's exactly what a host site resizing the iframe (e.g. into a modal)
    // changes.
    const checkWidth = () =>
      setIsWideEnoughForHd(window.innerWidth >= hdMinWidth);

    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, [hdMinWidth]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !hlsSource) return;

    const hdEnabled = isFullscreen || isWideEnoughForHd;
    const qualityLevels = player.qualityLevels();

    const updateQualityLevels = () => {
      for (let i = 0; i < qualityLevels.length; i++) {
        const level = qualityLevels[i];
        const isHd = level.height >= 1080;

        if (isHd) {
          level.enabled = hdEnabled;
        } else if (hdMinWidth !== undefined) {
          // Disabling the SD rungs is what makes the switch immediate:
          // it's a real state change, so VHS runs fastQualityChange_(),
          // dropping already-buffered SD content and refetching from the
          // playhead. Enabling the HD rung alone is a no-op whenever it
          // was already enabled, which leaves VHS's default several-second
          // ramp-up in place. Scoped to the width-gated (hdMinWidth) path
          // so other consumers keep their SD fallback rungs.
          level.enabled = !hdEnabled;
        }
      }
    };

    qualityLevels.on("addqualitylevel", updateQualityLevels);
    updateQualityLevels();

    if (hdEnabled) {
      // Seed VHS bandwidth high so ABR considers 1080p immediately.
      // VHS will adapt down naturally if real bandwidth can't sustain it.
      const tech = player.tech({ IWillNotUseThisInPlugins: true }) as
        | { vhs?: { bandwidth: number } }
        | undefined;
      if (tech?.vhs) {
        tech.vhs.bandwidth = 50_000_000;
      }
    }

    return () => {
      qualityLevels.off("addqualitylevel", updateQualityLevels);
    };
  }, [hlsSource, isFullscreen, isWideEnoughForHd, hdMinWidth]);

  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const player = playerRef.current;
      if (!player) return;

      if (event.code === "Space") {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement) {
          const tagName = activeElement.tagName.toLowerCase();
          const isFormElement =
            tagName === "input" ||
            tagName === "textarea" ||
            activeElement.contentEditable === "true";

          if (isFormElement) return;
        }

        event.preventDefault();
        event.stopPropagation();

        player.paused() ? player.play() : player.pause();
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, []);

  useEffect(() => {
    const player = playerRef.current;
    const onVideoPlay = () => {
      startTrackingSecondsWatched();
      if (!player?.currentTime() && onPlayStart) onPlayStart();
      if (onPlay) onPlay();

      const storage = volumeStorage === "local" ? localStorage : sessionStorage;
      const playerVolume = storage.getItem("playerVolume");
      if (playerVolume) {
        player?.volume(parseFloat(playerVolume));
      }

      analytics.track(`${eventSrc ? eventSrc : "Video Player"} - Play`, {
        ...playAnalyticsProperties,
        total_length: player?.duration(),
        file_extension: player?.currentSrc().split(".").pop(),
      });
    };

    player?.on("play", onVideoPlay);

    return () => player?.off("play", onVideoPlay);
  }, [
    onPlay,
    onPlayStart,
    playAnalyticsProperties,
    startTrackingSecondsWatched,
    eventSrc,
    volumeStorage,
  ]);

  useEffect(() => {
    const player = playerRef.current;
    const onPause = () => {
      stopTrackingSecondsWatched();
      if (onPlayPause && !player?.seeking()) {
        onPlayPause();
      }
    };

    player?.on("pause", onPause);

    return () => player?.off("pause", onPause);
  }, [stopTrackingSecondsWatched, onPlayPause]);

  useEffect(() => {
    const player = playerRef.current;
    const onVolumeChange = () => {
      // hasStarted() is false until the first successful play, which filters
      // out autoplay-strategy mute changes (the "any"/"muted" fallback fires
      // volumechange before the user has had a chance to interact).
      if (player?.hasStarted()) onMuteChange?.(!!player?.muted());

      // We only want to set volume if the user intended to change it, not via autoplay settings
      if (!player?.hasStarted()) return;

      const storage = volumeStorage === "local" ? localStorage : sessionStorage;
      storage.setItem(
        "playerVolume",
        player?.muted() ? "0.0" : `${player?.volume()}`,
      );
    };

    player?.on("volumechange", onVolumeChange);

    return () => player?.off("volumechange", onVolumeChange);
  }, [onMuteChange, volumeStorage]);

  useEffect(() => {
    const player = playerRef.current;
    const onEnded = () => {
      onPlayEnd && onPlayEnd(player);
      analytics.track(`${eventSrc ? eventSrc : "Video Player"} - End`, {
        ...watchAnalyticsProperties,
      });

      if (loop) {
        setSecondsWatched(0);
        setTrackedView(false);
        playerRef?.current?.play();
        analytics.track(`${eventSrc ? eventSrc : "Video Player"} - Looped`, {
          ...watchAnalyticsProperties,
        });
      }
    };

    player?.on("ended", onEnded);

    return () => player?.off("ended", onEnded);
  }, [loop, onPlayEnd, eventSrc, watchAnalyticsProperties]);

  useEffect(() => {
    const player = playerRef.current;
    const handleError = () => {
      analytics.track(`${eventSrc ? eventSrc : "Video Player"} - Error`, {
        ...watchAnalyticsProperties,
        errorCode: player?.error()?.code,
        errorMessage: player?.error()?.message,
      });
      onError?.(player);
    };

    player?.on("error", handleError);

    return () => player?.off("error", handleError);
  }, [onError, eventSrc, watchAnalyticsProperties]);

  useEffect(() => {
    if (secondsWatched >= 5 && !trackedView) {
      analytics.track(
        `${eventSrc ? eventSrc : "Video Player"} - Watched 5 seconds`,
        {
          ...watchAnalyticsProperties,
        },
      );
      setTrackedView(true);
      stopTrackingSecondsWatched();
    }
  }, [
    secondsWatched,
    stopTrackingSecondsWatched,
    trackedView,
    watchAnalyticsProperties,
    eventSrc,
  ]);

  return (
    <StyledPlayer {...props} data-fullscreen={isFullscreen}>
      <Box data-vjs-player>
        <Box ref={videoRef} />
      </Box>
    </StyledPlayer>
  );
};
